import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import Widget from 'app/components/common/Widget';
import { Button } from 'app/components/common/Button';
import { Icon } from 'app/components/common/Icon';
import { WidgetNavigate } from 'app/components/common/WidgetNavigate';
import { SteartTag } from 'app/components/agt/common/SteartTag';
import { ZielErgebnisBalken, ZielErgebnisBalkenModel } from 'app/components/charts/ZielErgebnisBalken';
import { BtStueckTimeModel, BtStueckTimeModelChart } from 'app/components/charts/BtStueckTimeChart';
import { formatCurrency } from 'app/utils';
import colors from 'assets/scss/colors.module.scss';
import routes from 'routes';
import { aspKategorieModifier, AspZuweisung, aspZuweisungTagModifier, buildAspPotenzialListe, formatAspDateShort, IAspAgtPotenzial, ASP_CAMPAIGNS } from './asp.mock.data';
import { AgenturASPModal, agenturAspModalId } from './AgenturASPModal';

// alle Widget-Aktionen navigieren im Design-Modus zurück auf diese Seite
// (lazy, da routes.ts diese Datei über den DesignContainer importiert)
const designPath = () => routes.DESIGN.getPath('asp-agt-dashboard');

// ASP-Widget für das Agentur-Dashboard: die aktiven (zugewiesenen) Kampagnen der Agentur
export const AgenturAspWidget = observer((props: { vm: IAspAgtPotenzial; onOpenModal: () => void }) => {
	const { vm, onOpenModal } = props;

	const aktiveCampaigns = ASP_CAMPAIGNS.filter((c) => vm.teilnahmen[c.id]);

	const action = (
		<Button className="button is-widget-header" onClick={onOpenModal}>
			Zuweisen
		</Button>
	);

	return (
		<Widget title="Aktivitätsschwerpunkte" action={action} modifier="is-paddingless">
			{aktiveCampaigns.length === 0 ? (
				<div className="has-text-centered">
					<Button className="button is-text is-padded" onClick={onOpenModal}>
						Aktivitätsschwerpunkte zuweisen
					</Button>
				</div>
			) : (
				<table className="table is-text">
					<tbody>
						{aktiveCampaigns.map((c) => (
							<tr key={c.id}>
								<td>
									<div className="asp-campaign-cell">
										<span className={`asp-color-bar  ${aspKategorieModifier[c.kategorie]}`} />
										<span>
											{c.name}
											<br />
											<small className="color-grey">
												{formatAspDateShort(c.start)} – {formatAspDateShort(c.end)}
											</small>
										</span>
									</div>
								</td>
								<td className="has-text-right">
									<strong>{vm.potenziale[c.id]}</strong>
									<br />
									<small className="color-grey">Potenzial</small>
								</td>
								<td className="has-text-right">
									<span className={`tag is-small ${aspZuweisungTagModifier[vm.teilnahmen[c.id]]}`}>{vm.teilnahmen[c.id]}</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</Widget>
	);
});

const MockUebersichtWidget = () => (
	<Widget title="Agentur Übersicht" modifier="is-paddingless">
		<table className="table is-text align-top">
			<tbody>
				<tr>
					<td>Prov.-modell</td>
					<td>
						<strong>Vertrag 2011 GV inkl. 36 Monate</strong>
					</td>
				</tr>
				<tr>
					<td>Segmentierung</td>
					<td>
						<div className="tag is-small agentur-segment-tag is-unternehmer">
							<div className="segment-value">Unternehmeragentur</div>
						</div>
					</td>
				</tr>
				<tr>
					<td>Geschäftsplan:</td>
					<td>
						<strong>GP GV/HV - Variante 70</strong>
					</td>
				</tr>
				<tr>
					<td>AP-Fonds:</td>
					<td>
						<strong>18 ‰</strong>
					</td>
				</tr>
				<tr>
					<td>Prov-Stufe AP:</td>
					<td>
						<strong>AP 4</strong>
					</td>
				</tr>
				<tr>
					<td>Prov-Stufe FP:</td>
					<td>
						<strong>FP 2 ab 2018</strong>
					</td>
				</tr>
				<tr>
					<td>
						<strong>Schadenregulierung</strong>
					</td>
					<td></td>
				</tr>
				<tr>
					<td>Vollmacht:</td>
					<td>
						<strong>k.A.</strong>
					</td>
				</tr>
				<tr>
					<td>
						<strong>Agentur Cluster</strong>
					</td>
					<td>
						<SteartTag id={934} name="VBL: 1" /> <SteartTag id={973} name="VAS: 3" /> <SteartTag id={977} name="BSG: 2" /> <SteartTag id={968} name="KVS: 2" />{' '}
						<SteartTag id={959} name="KME: 3" /> <SteartTag id={951} name="UB: 3" />
					</td>
				</tr>
			</tbody>
		</table>
	</Widget>
);

const MockKontaktDatenWidget = () => (
	<Widget title="Kontaktdaten" modifier="is-paddingless">
		<table className="table is-text">
			<tbody>
				<tr>
					<td className="full-width">
						<strong>GS München</strong>
					</td>
					<td>Telefon</td>
					<td className="has-text-left">
						<span className="button is-text is-padded">089 1234567</span>
					</td>
				</tr>
				<tr>
					<td>Musterstraße 12</td>
					<td>Mobil</td>
					<td className="has-text-left">
						<span className="button is-text is-padded">0171 2345678</span>
					</td>
				</tr>
				<tr>
					<td>80331 München</td>
					<td>E-Mail</td>
					<td className="has-text-left">
						<span className="button is-text is-padded">mueller.partner@allianz.de</span>
					</td>
				</tr>
				<tr>
					<td colSpan={3}>
						<Button className="button is-small is-turquoise is-inverted">
							<Icon iconClass="map-marker-alt" />
							<span>Auf Karte zeigen</span>
						</Button>
					</td>
				</tr>
			</tbody>
		</table>
	</Widget>
);

const MockExpertiseWidget = () => (
	<Widget title="Expertise" modifier="is-paddingless">
		<table className="table">
			<thead>
				<tr>
					<th>Thema</th>
					<th>Ablaufdatum</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Baufinanzierung</td>
					<td>31.12.2026</td>
				</tr>
				<tr>
					<td>Firmen Sach</td>
					<td>30.06.2027</td>
				</tr>
			</tbody>
		</table>
		<div className="has-text-centered">
			<Button type="button" className="button is-text is-padded is-small">
				Expertise Hinzufügen
			</Button>
		</div>
	</Widget>
);

const MockPersonalWidget = () => (
	<Widget title="Personal" modifier="is-paddingless">
		<table className="table is-compact">
			<thead>
				<tr>
					<th></th>
					<th>Name</th>
					<th>Geburtsdatum</th>
					<th></th>
					<th>Gründung / Eintritt</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td>Inhaber</td>
					<td>Max Müller</td>
					<td>14.07.1975</td>
					<td>
						<Icon iconClass="birthday-cake" />
					</td>
					<td>01.01.2005</td>
				</tr>
				<tr>
					<td>Vertriebsassistent/in</td>
					<td>Petra Partner</td>
					<td>03.02.1982</td>
					<td></td>
					<td>15.08.2012</td>
				</tr>
				<tr>
					<td>Bürokraft</td>
					<td>Sabine Schmidt</td>
					<td>21.11.1990</td>
					<td></td>
					<td>01.04.2019</td>
				</tr>
			</tbody>
		</table>
		<div className="has-text-centered">
			<Button type="button" className="button is-text is-padded is-small">
				Mitarbeiter Hinzufügen
			</Button>
		</div>
	</Widget>
);

const MOCK_BETREUER = [
	{ steart: 1003, name: 'Julia Beispiel', bnrId: 7025257 },
	{ steart: 959, name: 'Thomas Muster', bnrId: 7021728 },
	{ steart: 951, name: 'Vogel Andreas', bnrId: 7021737 },
	{ steart: 973, name: 'Max Mustermann', bnrId: 7023508 },
	{ steart: 755, name: 'Erika Musterfrau', bnrId: 7022243 },
	{ steart: 934, name: 'S. Schmidt', bnrId: 7022721 },
	{ steart: 977, name: 'Bauer Petra', bnrId: 7023968 },
	{ steart: 968, name: 'Frank Fischer', bnrId: 7021731 },
];

const MockBetreuerWidget = () => (
	<Widget title="Betreuer">
		<div className="agt-betreuer-widget">
			{MOCK_BETREUER.map((u) => (
				<span className="betreuer-widget-item" key={u.bnrId}>
					<SteartTag id={u.steart} />
					<span className="name">
						{u.name} ({u.bnrId})
					</span>
				</span>
			))}
		</div>
	</Widget>
);

// 12 Monate Bewertung (bt) und Stück (st)
const MOCK_VERLAUF = [
	{ bmLabel: '2025-07', bt: 42000, st: 78 },
	{ bmLabel: '2025-08', bt: 96000, st: 55 },
	{ bmLabel: '2025-09', bt: 28000, st: 60 },
	{ bmLabel: '2025-10', bt: 31000, st: 84 },
	{ bmLabel: '2025-11', bt: 34000, st: 74 },
	{ bmLabel: '2025-12', bt: 41000, st: 98 },
	{ bmLabel: '2026-01', bt: 12000, st: 52 },
	{ bmLabel: '2026-02', bt: 79000, st: 88 },
	{ bmLabel: '2026-03', bt: 30000, st: 82 },
	{ bmLabel: '2026-04', bt: 24000, st: 61 },
	{ bmLabel: '2026-05', bt: 38000, st: 40 },
	{ bmLabel: '2026-06', bt: 71000, st: 9 },
];

const MockBewertungsverlaufWidget = () => {
	const model = new BtStueckTimeModel({
		title: 'Bewertung',
		fill: colors.turquoise,
		btKey: 'bt',
		btLabel: 'Bewertung',
		stKey: 'st',
		stLabel: 'Stück',
		timeKey: 'bmLabel',
		timeLabel: 'Monat',
	});
	model.data = MOCK_VERLAUF as any;
	return (
		<Widget title="Bewertungsverlauf" action={<WidgetNavigate text={'Zum Bericht'} path={designPath()} />}>
			<BtStueckTimeModelChart {...model} />
		</Widget>
	);
};

const MockEingangshuerdeWidget = () => {
	const model = new ZielErgebnisBalkenModel(200000, 325372);
	return (
		<Widget title="Eingangshürde AP3">
			<ZielErgebnisBalken model={model} />
		</Widget>
	);
};

const MockFitnessWidget = () => (
	<Widget title="Agentur der Zukunft" modifier="is-paddingless" action={<WidgetNavigate text={'Zum Bericht'} path={designPath()} />}>
		<div className="pad-1rem pad-bottom-0">
			<h4 className="title">Fitness Tracker</h4>
		</div>
		<div className="fitness-widget-grid">
			<div className="fitness-grid-item bg-green">
				<div className="title">GUP</div>
				<div className="value">100 %</div>
			</div>
			<div className="fitness-grid-item bg-turquoise">
				<div className="title">AMIS.NOW</div>
				<div className="value">81 %</div>
			</div>
			<div className="fitness-grid-item bg-yellow">
				<div className="title">AKUMA Planer</div>
				<div className="value">0 %</div>
			</div>
			<div className="fitness-grid-item bg-blue">
				<div className="title">mAZ</div>
				<div className="value">40 %</div>
			</div>
		</div>
		<div className="pad-1rem grid is-col-half">
			<Button className="button is-primary is-inverted is-block is-marginless">
				<span>Fitness Tracker PDF</span>
			</Button>
			<Button className="button is-primary is-inverted is-block is-marginless">
				<span>Fitness Plan PDF</span>
			</Button>
		</div>
	</Widget>
);

const MockBestandWidget = () => (
	<Widget title="Bestand" modifier="is-paddingless" action={<WidgetNavigate text={'Zum Bericht'} path={designPath()} />}>
		<table className="table last-col-right">
			<tbody>
				<tr>
					<td>
						<strong>
							Sach gesamt <br />
							(ohne AGCS/mit ESA)
						</strong>
					</td>
					<td>Stichtag 01.06.2026</td>
				</tr>
				<tr>
					<td>
						<strong>Beitrag Stich.</strong>
					</td>
					<td>{formatCurrency(1662341)}</td>
				</tr>
				<tr>
					<td>
						<strong>Beitrag Zuwachs</strong>
					</td>
					<td>{formatCurrency(135245)}</td>
				</tr>
				<tr>
					<td>
						<strong>Stück Stich.</strong>
					</td>
					<td>{formatCurrency(2570)}</td>
				</tr>
				<tr>
					<td>
						<strong>Stück Zuwachs</strong>
					</td>
					<td>{formatCurrency(13)}</td>
				</tr>
			</tbody>
		</table>
	</Widget>
);

const MockGeburtstageWidget = () => (
	<Widget title="Anstehende Geburtstage" modifier="is-paddingless" action={<WidgetNavigate text={'Alle'} path={designPath()} />}>
		<table className="table">
			<tbody>
				<tr>
					<td>
						<Icon iconClass="birthday-cake" />
					</td>
					<td>Max Müller</td>
					<td className="has-text-right">14.07. (51 Jahre)</td>
				</tr>
			</tbody>
		</table>
	</Widget>
);

const MOCK_DOCS = [
	{ date: '15.01.2026', title: 'Jahresplanungsgespräch 2026' },
	{ date: '28.11.2025', title: 'Besprechung Kinderpolice' },
	{ date: '17.10.2025', title: 'Besprechung Bestattungsschutz' },
	{ date: '01.03.2025', title: 'GV Müller - Interaktion VAS' },
];

const MockDokumenteWidget = () => (
	<Widget title="Letzte Dokumente" modifier="is-paddingless" action={<WidgetNavigate text={'Alle'} path={designPath()} />}>
		<table className="table">
			<tbody>
				{MOCK_DOCS.map((d) => (
					<tr key={d.title}>
						<td>{d.date}</td>
						<td>{d.title}</td>
						<td className="has-text-right">
							<Button className="button is-text is-small">Anzeigen</Button>
						</td>
					</tr>
				))}
			</tbody>
		</table>
	</Widget>
);

const MockMassnahmenWidget = () => (
	<Widget title="Offene Maßnahmen" modifier="is-paddingless" action={<WidgetNavigate text={'Alle'} path={designPath()} />}>
		<table className="table">
			<tbody>
				<tr>
					<td>31.07.2026</td>
					<td>Kleinbetriebe: Terminierung starten</td>
				</tr>
				<tr>
					<td>15.08.2026</td>
					<td>Rückmeldung MidCorp</td>
				</tr>
			</tbody>
		</table>
	</Widget>
);

export const AspAgtDashboard = observer(() => {
	const { uiStore } = useStore();
	const [vm, setVm] = useState<IAspAgtPotenzial>(() => buildAspPotenzialListe()[0]);

	// Modal beim Verlassen der Seite schließen
	useEffect(() => {
		return () => uiStore.hideModal(agenturAspModalId);
	}, [uiStore]);

	const openModal = () => {
		uiStore.showModal(agenturAspModalId);
	};

	const onSaveAssignments = (assigned: Record<string, AspZuweisung>) => {
		setVm((prev) => ({ ...prev, teilnahmen: assigned }));
	};

	return (
		<>
			<div className="dashboard has-bigger-left is-agentur">
				<div className="left">
					<MockUebersichtWidget />
					<MockKontaktDatenWidget />
					<MockExpertiseWidget />
					<MockPersonalWidget />
					<MockBetreuerWidget />
					<MockBewertungsverlaufWidget />
				</div>
				<div className="right">
					<AgenturAspWidget vm={vm} onOpenModal={openModal} />
					<MockEingangshuerdeWidget />
					<MockFitnessWidget />
					<MockBestandWidget />
					<MockGeburtstageWidget />
					<MockDokumenteWidget />
					<MockMassnahmenWidget />
				</div>
			</div>
			<AgenturASPModal vm={vm} onSave={onSaveAssignments} />
		</>
	);
});
